import type { GameContext, FacetKey } from "@/core/data/types";

/**
 * Facet pools that compose a random game concept. Deliberately broad — extend
 * freely (the Content Studio in Phase 4 will make these editable in-app). These
 * also power the Game-Jam Idea Generator (Pillar 3).
 */

export const GENRES = [
  "Action", "Adventure", "Platformer", "RPG", "Action-RPG", "JRPG", "Shooter",
  "First-Person Shooter", "Third-Person Shooter", "Fighting", "Racing", "Sports",
  "Simulation", "Strategy", "Real-Time Strategy", "Turn-Based Strategy", "Puzzle",
  "Survival", "Horror", "Stealth", "Sandbox", "Roguelike", "Metroidvania",
  "Visual Novel", "Rhythm", "Card Game", "MOBA", "MMORPG", "Battle Royale",
  "Tower Defense", "Point-and-Click", "Idle / Incremental", "Party Game",
  "Tycoon / Management", "Open World",
];

export const SUBGENRES = [
  "Soulslike", "Looter-Shooter", "Hero Shooter", "Extraction Shooter",
  "Boomer Shooter", "Survival Horror", "Psychological Horror", "Immersive Sim",
  "Walking Simulator", "City Builder", "Colony Sim", "Life Sim", "Farming Sim",
  "Dating Sim", "Deckbuilder", "Auto-Battler", "Bullet Hell", "Twin-Stick Shooter",
  "Hack-and-Slash", "Beat 'em Up", "Run-and-Gun", "Puzzle-Platformer",
  "Precision Platformer", "4X", "Grand Strategy", "Tactical RPG", "Dungeon Crawler",
  "Action Roguelike", "Survival Crafting", "Social Deduction", "Asymmetric PvP",
  "Sokoban", "Match-3", "Endless Runner", "Rhythm-Action", "Open-World Sandbox",
];

export const PLATFORMS = [
  "PC (Steam)", "PlayStation 5", "PlayStation 4", "Xbox Series X|S", "Nintendo Switch",
  "Switch 2", "iOS", "Android", "Steam Deck", "Meta Quest (VR)", "PlayStation VR2",
  "Web Browser", "Super Nintendo (retro)", "Sega Genesis (retro)", "Game Boy (retro)",
  "Nintendo 64 (retro)", "Arcade Cabinet", "Smart TV", "Handheld PC", "Apple Vision Pro",
];

export const GAME_MODES = [
  "Single-player", "Local Co-Op (couch)", "Online Co-Op", "PvP", "PvE",
  "Competitive Ranked", "4-player Party", "Massively Multiplayer", "Split-Screen",
  "Asynchronous Multiplayer", "1v1 Duel", "Free-for-All", "Team vs Team",
  "Drop-in / Drop-out Co-Op", "Solo + Optional Online", "Battle Royale (many players)",
];

export const CAMERAS = [
  "First Person", "Third Person (over-the-shoulder)", "Top-Down", "Isometric",
  "2D Side-Scroller", "2.5D", "Fixed / Cinematic", "Bird's-Eye / Tactical",
  "First-Person VR", "Twin-Stick (top-down)", "Side-On Versus", "Free Orbit",
  "Rail / On-Rails", "Scrolling Vertical",
];

export const THEMES = [
  "High Fantasy", "Dark Fantasy", "Sci-Fi", "Cyberpunk", "Steampunk", "Post-Apocalyptic",
  "Horror", "Cosmic Horror", "Noir", "Western", "Mythology (Greek)", "Mythology (Norse)",
  "Feudal Japan", "Pirates / High Seas", "Space Opera", "Solarpunk", "Dieselpunk",
  "Medieval", "Modern Military", "Prehistoric", "Underwater", "Heist / Crime",
  "Supernatural", "Fairytale", "Dystopian Corporate", "Wild West Weird", "Afrofuturism",
];

export const MECHANICS = [
  "Double-Jump", "Wall-Run", "Grappling Hook", "Time Rewind", "Bullet-Time",
  "Gravity Flip", "Invisibility / Cloak", "Possession / Body-Swap", "Shape-Shifting",
  "Portals", "Teleport-Dash", "Parry / Riposte", "Crafting", "Base-Building",
  "Deck-Building", "Dialogue Trees", "Stealth Takedowns", "Hacking Mini-Game",
  "Companion AI", "Day/Night Cycle", "Weather Manipulation", "Summoning",
  "Weapon Crafting", "Elemental Combos", "Momentum-Based Movement", "Hookshot Swing",
  "Cover System", "Bullet Reflection", "Slow-Mo Aim", "Resource Drain", "Possessed Terrain",
  "Tower Placement", "Tile Matching", "Card Drafting", "Dice Rolling", "Permadeath",
  "Roguelike Runs", "Tether / Linked Bodies", "Sanity Meter", "Heat / Wanted Level",
  "Morph Ball / Tight Spaces", "Echolocation / Sonar", "Photo Mode as Tool",
  "Rhythm-Locked Actions", "Sound-Based Stealth", "Limited Inventory (Tetris)",
  "Time Loop (Groundhog Day)", "Diegetic Map (no UI)", "One-Hit Death", "Co-op Tethered Lives",
];

/** Core emotion / aesthetic the design should evoke (MDA aesthetics). */
export const EMOTIONS = [
  "Tension / Dread", "Wonder / Awe", "Triumph (Fiero)", "Fellowship / Belonging",
  "Mastery / Competence", "Catharsis", "Curiosity / Discovery", "Power Fantasy",
  "Serenity / Calm", "Melancholy", "Comedy / Delight", "Fear / Horror",
  "Nostalgia", "Justice / Righteousness", "Greed / Temptation", "Sublime Loneliness",
  "Hope", "Sacrifice / Loss", "Flow / In the Zone", "Surprise / Subversion",
];

/** Target audience the design must serve. */
export const AUDIENCES = [
  "Young Kids (E)", "Teens", "Core / Hardcore", "Casual / Mainstream", "Lapsed Players",
  "Esports Competitors", "Cozy-Game Fans", "Speedrunners", "Streamers & Their Chat",
  "Solo Story Seekers", "Couch Co-op Groups", "Mobile Commuters", "Genre Newcomers",
  "Completionists", "Roleplayers", "Min-Maxers / Theorycrafters", "Accessibility-First Players",
];

/** Visual direction / art style. */
export const ART_STYLES = [
  "Pixel Art", "Low-Poly", "Cel-Shaded", "Photorealistic", "Hand-Painted",
  "Flat / Minimalist", "Voxel", "Claymation / Stop-Motion", "Ink / Sumi-e",
  "Vaporwave / Neon", "Retro CRT", "Paper / Papercraft", "Comic / Cel Panels",
  "Silhouette / Limbo-like", "Surreal / Dreamlike", "Toon / Cartoony",
  "Gritty Realism", "Pastel / Storybook", "Monochrome + 1 Color",
];

/** Ordered axis metadata + the pool for each. Single source for the picker UI. */
export const FACET_LABELS: Record<FacetKey, string> = {
  genre: "Genre",
  subgenre: "Subgenre",
  platform: "Platform",
  gameMode: "Game Mode",
  camera: "Camera",
  theme: "Theme",
  mechanic: "Mechanic",
  emotion: "Core Emotion",
  audience: "Target Audience",
  artStyle: "Art Style",
};

export const FACET_POOLS: Record<FacetKey, string[]> = {
  genre: GENRES,
  subgenre: SUBGENRES,
  platform: PLATFORMS,
  gameMode: GAME_MODES,
  camera: CAMERAS,
  theme: THEMES,
  mechanic: MECHANICS,
  emotion: EMOTIONS,
  audience: AUDIENCES,
  artStyle: ART_STYLES,
};

export const FACET_ORDER = Object.keys(FACET_LABELS) as FacetKey[];

/** A selection maps each *enabled* axis (built-in or custom) to its allowed values. */
export type FacetSelection = Record<string, string[]>;

function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

/**
 * Roll a concept honoring the user's selection: each axis in `selection` (with at
 * least one allowed value) is rolled, in the selection's own key order. With no
 * selection, every built-in axis rolls from its full pool.
 */
export function rollContext(selection?: FacetSelection): GameContext {
  const ctx: GameContext = {};
  if (selection) {
    for (const [key, values] of Object.entries(selection)) {
      if (values && values.length > 0) ctx[key] = pick(values);
    }
  } else {
    for (const key of FACET_ORDER) ctx[key] = pick(FACET_POOLS[key]);
  }
  return ctx;
}

/** Joins the rolled values in their insertion (axis) order. */
export function contextSummary(c: GameContext): string {
  return Object.values(c).filter(Boolean).join(" · ");
}
